/**
 * RoomForm Component
 * Step 2: Room information form
 */

import { PoolTable3DViewer } from './PoolTable3DViewer.js';

export class RoomForm {
  constructor(containerId, initialData = {}, onValidate, onDataChange, tableData = {}) {
    this.container = document.getElementById(containerId);
    this.data = { ...initialData };
    this.tableData = tableData; // Table data for visualization
    this.onValidate = onValidate;
    this.onDataChange = onDataChange;
    this.errors = {};
    this.touchedFields = {}; // Track which fields have been interacted with
    this.viewer = null;
    this.viewerExpanded = true; // Track whether 3D preview is expanded (shown by default)
    this.render();
  }

  render() {
    // Destroy existing viewer before re-rendering to prevent memory leaks
    if (this.viewer) {
      this.viewer.destroy();
      this.viewer = null;
    }
    
    this.container.innerHTML = `
      <div class="form-container">
        <h2 class="form-title">Room Information</h2>
        <p class="form-description">Provide details about your installation space.</p>
        
        <div class="form-grid">
          <div class="form-group">
            <label for="ceiling-height" class="form-label">
              Ceiling Height <span class="required">*</span>
              <button type="button" class="info-icon" id="ceiling-height-info" aria-label="Ceiling height information">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="1.5"/>
                  <path d="M8 12V8M8 4H8.01" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                </svg>
              </button>
            </label>
            <div class="slider-input-group">
              <input
                type="range"
                id="ceiling-height-slider"
                class="form-slider"
                min="80"
                max="120"
                step="1"
                value="${this.data.ceilingHeight || 100}"
              />
              <div class="slider-labels">
                <span>80"</span>
                <span>120"</span>
              </div>
              <div class="input-wrapper">
                <input
                  type="number"
                  id="ceiling-height"
                  class="form-input ${this.errors.ceilingHeight ? 'error' : ''}"
                  placeholder="100"
                  min="80"
                  max="120"
                  step="1"
                  inputmode="numeric"
                  value="${this.data.ceilingHeight || ''}"
                  required
                />
                <span class="input-unit">inches</span>
              </div>
            </div>
            ${this.errors.ceilingHeight ? `<div class="form-error">${this.errors.ceilingHeight}</div>` : ''}
          </div>
          
          <!-- Modal for ceiling height info -->
          <div class="modal-overlay" id="ceiling-height-modal" style="display: none;">
            <div class="modal-content">
              <div class="modal-header">
                <h3>Ceiling Height Information</h3>
                <button type="button" class="modal-close" id="ceiling-height-modal-close" aria-label="Close">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                  </svg>
                </button>
              </div>
              <div class="modal-body">
                <p>Our standard installation range is <strong>80 to 120 inches</strong> (6.7 to 10 feet).</p>
                <ul>
                  <li><strong>Shorter than 80 inches:</strong> If your ceiling is shorter than 80 inches, there may be an issue with your house structure.</li>
                  <li><strong>Taller than 120 inches:</strong> If your ceiling is taller than 120 inches, this will require a custom installation.</li>
                </ul>
              </div>
            </div>
          </div>

          <div class="form-group">
            <label for="mounting-option" class="form-label">
              Mounting Position <span class="required">*</span>
            </label>
            <select
              id="mounting-option"
              class="form-select ${this.errors.mountingOption ? 'error' : ''}"
              required
            >
              <option value="">Select mounting position</option>
              <option value="ceiling" ${this.data.mountingOption === 'ceiling' ? 'selected' : ''} ${this.data.ceilingHeight && this.data.ceilingHeight < 90 ? 'disabled' : ''}>Ceiling Mount</option>
              <option value="side" ${this.data.mountingOption === 'side' ? 'selected' : ''}>Side Mount</option>
              <option value="end" ${this.data.mountingOption === 'end' ? 'selected' : ''}>End Mount</option>
            </select>
            ${this.errors.mountingOption ? `<div class="form-error">${this.errors.mountingOption}</div>` : ''}
            ${this.data.ceilingHeight && this.data.ceilingHeight < 90 ? '<p class="form-hint" style="color: #ef4444;">Ceiling mount is not available for ceilings less than 90 inches.</p>' : '<p class="form-hint">How would you like to mount the training system?</p>'}
          </div>

          <div class="form-group full-width">
            <label class="form-label">Additional Considerations</label>
            <div class="checkbox-group">
              <label class="checkbox-label">
                <input
                  type="checkbox"
                  class="form-checkbox"
                  ${this.data.hasObstructions ? 'checked' : ''}
                />
                <span>Room has obstructions (beams, fans, etc.)</span>
              </label>
              <label class="checkbox-label">
                <input
                  type="checkbox"
                  class="form-checkbox"
                  ${this.data.needsCableManagement ? 'checked' : ''}
                />
                <span>Requires cable management</span>
              </label>
              <label class="checkbox-label">
                <input
                  type="checkbox"
                  class="form-checkbox"
                  ${this.data.hasMultipleTables ? 'checked' : ''}
                />
                <span>Multiple tables in same room</span>
              </label>
            </div>
          </div>
        </div>
        
        <div class="viewer-toggle-section" style="margin-top: 2rem;">
          <button class="viewer-toggle-btn" id="room-viewer-toggle" type="button">
            <span class="viewer-toggle-text">${this.viewerExpanded ? 'Hide 3D Preview' : 'Show 3D Preview'}</span>
            <span class="viewer-toggle-icon">${this.viewerExpanded ? '▲' : '▼'}</span>
          </button>
          <div class="viewer-toggle-content" id="room-viewer-content" style="display: ${this.viewerExpanded ? 'block' : 'none'};">
            <div id="room-3d-viewer-container"></div>
          </div>
        </div>
      </div>
    `;

    // Setup slider and number input for ceiling height with debouncing
    const ceilingHeightSlider = this.container.querySelector('#ceiling-height-slider');
    const ceilingHeightInput = this.container.querySelector('#ceiling-height');
    let ceilingHeightInputTimeout = null;

    if (ceilingHeightSlider && ceilingHeightInput) {
      // Track if user is actively dragging
      let isDragging = false;
      
      // Function to update slider fill color based on value
      const updateSliderFill = () => {
        const value = parseInt(ceilingHeightSlider.value);
        const min = parseInt(ceilingHeightSlider.min) || 80;
        const max = parseInt(ceilingHeightSlider.max) || 120;
        const percentage = ((value - min) / (max - min)) * 100;
        ceilingHeightSlider.style.background = `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${percentage}%, #e2e8f0 ${percentage}%, #e2e8f0 100%)`;
      };
      
      // Initialize the fill on render
      updateSliderFill();
      
      // Slider updates number input immediately (works for both mouse and touch)
      ceilingHeightSlider.addEventListener('input', () => {
        const value = parseInt(ceilingHeightSlider.value);
        ceilingHeightInput.value = value;
        isDragging = true;
        this.touchedFields.ceilingHeight = true;
        updateSliderFill(); // Update the visual fill
        this.handleInputChange();
        // Don't validate while dragging - it causes re-renders and interrupts dragging
      });
      
      // Also update on change for better mobile support (when user releases)
      ceilingHeightSlider.addEventListener('change', () => {
        const value = parseInt(ceilingHeightSlider.value);
        ceilingHeightInput.value = value;
        isDragging = false;
        this.touchedFields.ceilingHeight = true;
        updateSliderFill(); // Update the visual fill
        this.handleInputChange();
        // Validate only when user finishes dragging
        this.validate();
      });
      
      // Handle touch events for better mobile dragging
      ceilingHeightSlider.addEventListener('touchstart', () => {
        isDragging = true;
      }, { passive: true });
      
      ceilingHeightSlider.addEventListener('touchend', () => {
        isDragging = false;
      }, { passive: true });
      
      // Handle mouse events
      ceilingHeightSlider.addEventListener('mousedown', () => {
        isDragging = true;
      });
      
      ceilingHeightSlider.addEventListener('mouseup', () => {
        isDragging = false;
      });

      // Number input - only update data while typing, no validation (validation only on blur)
      ceilingHeightInput.addEventListener('input', () => {
        // Clear existing timeout
        if (ceilingHeightInputTimeout) {
          clearTimeout(ceilingHeightInputTimeout);
        }

        // Debounce data update - wait 500ms after user stops typing
        // Don't validate or clamp here - allow user to type any number without losing focus
        ceilingHeightInputTimeout = setTimeout(() => {
          this.touchedFields.ceilingHeight = true;
          this.handleInputChange();
          // Don't call validate() here - it re-renders and causes focus loss
        }, 500);
      });

      // Select all text on focus for easy overwriting (especially on mobile)
      ceilingHeightInput.addEventListener('focus', (e) => {
        // Use setTimeout to ensure the selection happens after the default focus behavior
        setTimeout(() => {
          e.target.select();
        }, 0);
      });

      // Update slider when user presses Enter (finishes typing)
      ceilingHeightInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          ceilingHeightInput.blur(); // This will trigger the blur handler which updates the slider
        }
      });

      // Validate and clamp only on blur (when user is truly done typing)
      ceilingHeightInput.addEventListener('blur', () => {
        if (ceilingHeightInputTimeout) {
          clearTimeout(ceilingHeightInputTimeout);
          ceilingHeightInputTimeout = null;
        }
        
        // Only clamp if there's a valid number value
        const inputValue = ceilingHeightInput.value.trim();
        if (inputValue !== '') {
          const value = parseInt(inputValue);
          if (!isNaN(value) && value > 0) {
            const clampedValue = Math.max(80, Math.min(120, value));
            if (clampedValue !== value) {
              ceilingHeightInput.value = clampedValue;
              ceilingHeightSlider.value = clampedValue;
            } else {
              // Even if value is in range, update slider to match
              ceilingHeightSlider.value = value;
            }
            updateSliderFill(); // Update the visual fill
          }
        }
        
        this.touchedFields.ceilingHeight = true;
        this.handleInputChange();
        this.validate();
      });
    }

    // Setup modal for ceiling height info
    const ceilingHeightInfoBtn = this.container.querySelector('#ceiling-height-info');
    const ceilingHeightModal = this.container.querySelector('#ceiling-height-modal');
    const ceilingHeightModalClose = this.container.querySelector('#ceiling-height-modal-close');
    
    if (ceilingHeightInfoBtn && ceilingHeightModal) {
      // Open modal
      ceilingHeightInfoBtn.addEventListener('click', (e) => {
        e.preventDefault();
        ceilingHeightModal.style.display = 'flex';
      });
      
      // Close modal on close button
      if (ceilingHeightModalClose) {
        ceilingHeightModalClose.addEventListener('click', () => {
          ceilingHeightModal.style.display = 'none';
        });
      }
      
      // Close modal on overlay click
      ceilingHeightModal.addEventListener('click', (e) => {
        if (e.target === ceilingHeightModal) {
          ceilingHeightModal.style.display = 'none';
        }
      });
      
      // Close modal on Escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && ceilingHeightModal.style.display === 'flex') {
          ceilingHeightModal.style.display = 'none';
        }
      });
    }

    // Add event listeners for other inputs
    const mountingOptionSelect = this.container.querySelector('#mounting-option');
    
    if (mountingOptionSelect) {
      mountingOptionSelect.addEventListener('change', () => {
        this.touchedFields.mountingOption = true;
        this.handleInputChange();
        this.validate();
      });
    }

    // Checkbox listeners
    const checkboxes = this.container.querySelectorAll('.form-checkbox');
    checkboxes.forEach((checkbox) => {
      checkbox.addEventListener('change', () => this.handleInputChange());
    });
    
    // Setup viewer toggle
    const viewerToggle = this.container.querySelector('#room-viewer-toggle');
    const viewerContent = this.container.querySelector('#room-viewer-content');
    if (viewerToggle && viewerContent) {
      viewerToggle.addEventListener('click', () => {
        this.viewerExpanded = !this.viewerExpanded;
        viewerContent.style.display = this.viewerExpanded ? 'block' : 'none';
        const toggleText = viewerToggle.querySelector('.viewer-toggle-text');
        const toggleIcon = viewerToggle.querySelector('.viewer-toggle-icon');
        if (toggleText) {
          toggleText.textContent = this.viewerExpanded ? 'Hide 3D Preview' : 'Show 3D Preview';
        }
        if (toggleIcon) {
          toggleIcon.textContent = this.viewerExpanded ? '▲' : '▼';
        }
        
        // Initialize viewer if it's being shown for the first time
        if (this.viewerExpanded && !this.viewer) {
          this.initViewer();
        }
      });
    }
    
    // Initialize viewer if it's already expanded (e.g., after re-render)
    if (this.viewerExpanded && !this.viewer) {
      this.initViewer();
    }
  }
  
  initViewer() {
    const viewerContainer = this.container.querySelector('#room-3d-viewer-container');
    if (!viewerContainer) {
      console.warn('Viewer container not found');
      return;
    }
    
    if (this.viewer) {
      // Viewer already exists, just update it
      this.updateViewer();
      return;
    }
    
    try {
      // Create new viewer
      this.viewer = new PoolTable3DViewer(viewerContainer, {
        width: 800,
        height: 400
      });
      // Update after a small delay to ensure Three.js is initialized
      setTimeout(() => {
        if (this.viewer) {
          this.updateViewer();
        }
      }, 100);
    } catch (error) {
      console.error('Error initializing 3D viewer:', error);
      // Viewer will show its own error message
    }
  }
  
  updateViewer() {
    if (!this.viewer) {
      // If viewer doesn't exist, try to initialize it
      this.initViewer();
      return;
    }
    
    try {
      const params = {
        tableLength: this.tableData.length,
        tableWidth: this.tableData.width,
        tableHeightInches: this.tableData.heightInches,
        ceilingHeightInches: this.data.ceilingHeight || null,
        mountingOption: this.data.mountingOption || null,
      };
      
      this.viewer.updateParams(params);
    } catch (error) {
      console.error('Error updating viewer:', error);
    }
  }
  
  setTableData(tableData) {
    this.tableData = tableData;
    this.updateViewer();
  }

  handleInputChange() {
    const checkboxes = this.container.querySelectorAll('.form-checkbox');
    const ceilingHeight = parseFloat(this.container.querySelector('#ceiling-height').value) || null;
    const mountingOptionSelect = this.container.querySelector('#mounting-option');
    const mountingOption = mountingOptionSelect ? mountingOptionSelect.value || null : null;
    
    // If ceiling height is less than 90 and ceiling mount is selected, clear it
    if (ceilingHeight && ceilingHeight < 90 && mountingOption === 'ceiling') {
      if (mountingOptionSelect) {
        mountingOptionSelect.value = '';
      }
      this.data.mountingOption = null;
    }
    
    this.data = {
      ceilingHeight: ceilingHeight,
      mountingOption: mountingOptionSelect ? mountingOptionSelect.value || null : null,
      hasObstructions: checkboxes[0]?.checked || false,
      needsCableManagement: checkboxes[1]?.checked || false,
      hasMultipleTables: checkboxes[2]?.checked || false,
    };

    if (this.onDataChange) {
      this.onDataChange(this.data);
    }
    
    // Update viewer when data changes
    this.updateViewer();
  }

  validate(validateAllFields = false) {
    this.errors = {};

    const ceilingHeight = this.data.ceilingHeight;
    const mountingOption = this.data.mountingOption;

    // Check if all required fields have been touched
    const allFieldsTouched = this.touchedFields.ceilingHeight && this.touchedFields.mountingOption;
    
    // Determine if we should validate all fields (either explicitly requested, or all fields have been touched)
    const shouldValidateAll = validateAllFields || allFieldsTouched;

    // Only show errors for touched fields (for display purposes)
    // But validate all fields if they've all been touched (for button state)
    if (shouldValidateAll || this.touchedFields.ceilingHeight || ceilingHeight) {
      if (!ceilingHeight || ceilingHeight <= 0) {
        this.errors.ceilingHeight = 'Ceiling height is required and must be greater than 0';
      } else if (ceilingHeight < 80 || ceilingHeight > 120) {
        this.errors.ceilingHeight = 'Ceiling height must be between 80 and 120 inches';
      }
    }

    if (shouldValidateAll || this.touchedFields.mountingOption) {
      if (!mountingOption) {
        this.errors.mountingOption = 'Please select a mounting option';
      } else if (mountingOption === 'ceiling' && ceilingHeight && ceilingHeight < 90) {
        this.errors.mountingOption = 'Ceiling mount is not available for ceilings less than 90 inches';
      }
    }

    this.render();

    // For button state: form is valid only if all required fields have been touched AND are valid
    // If not all fields have been touched, form is not valid yet
    const hasErrors = Object.keys(this.errors).length > 0;
    const isValid = allFieldsTouched && !hasErrors;
    
    if (this.onValidate) {
      this.onValidate(isValid);
    }

    return isValid;
  }

  getData() {
    return this.data;
  }

  setData(data) {
    this.data = { ...data };
    this.render();
    this.updateViewer();
  }
  
  destroy() {
    if (this.viewer) {
      this.viewer.destroy();
      this.viewer = null;
    }
  }
}

